import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Download, Save, Loader2, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BulkLabelExcelPage = () => {
  const [params] = useSearchParams();
  const entryId = params.get("entry");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ filename: "source.xlsx", invoice_no: "", company_name: "" });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!entryId) {
      setError("Missing entry id in URL — expected ?entry=<id>");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API}/bulk-label/history/${entryId}/source-rows`,
          { headers: getAuthHeader() }
        );
        setHeaders(res.data?.headers || []);
        setRows(res.data?.rows || []);
        setMeta({
          filename: res.data?.filename || "source.xlsx",
          invoice_no: res.data?.invoice_no || "",
          company_name: res.data?.company_name || "",
        });
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load source rows");
      } finally {
        setLoading(false);
      }
    })();
  }, [entryId]);

  // Update a single cell when user finishes editing (blur or Enter)
  const updateCell = useCallback((rowIdx, colIdx, value) => {
    setRows(prev => {
      const next = prev.map(r => [...r]);
      if (next[rowIdx]) next[rowIdx][colIdx] = value;
      return next;
    });
    setDirty(true);
  }, []);

  const handleDownload = () => {
    try {
      const sheetData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const baseName = (meta.filename || "source").replace(/\.[^.]+$/, "");
      const suffix = dirty ? "_edited" : "";
      XLSX.writeFile(wb, `${baseName}${suffix}.xlsx`);
      toast.success(`Downloaded ${baseName}${suffix}.xlsx`);
    } catch (e) {
      toast.error("Download failed: " + (e.message || e));
    }
  };

  // Warn the user if they close with unsaved edits
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved edits — download to keep them.";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading source rows…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-md text-center shadow-sm">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <h2 className="font-semibold text-slate-800 mb-1">Could not open Excel view</h2>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bulk-label-excel-page">
      {/* Toolbar */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{meta.filename}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {meta.company_name && <span>{meta.company_name}</span>}
                {meta.invoice_no && <span> · Invoice {meta.invoice_no}</span>}
                {dirty && <span className="ml-2 text-amber-600 font-medium">• unsaved edits</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 hidden sm:inline">
              Double-click any cell to edit
            </span>
            <Button
              onClick={handleDownload}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="excel-page-download"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Spreadsheet */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-auto">
          <table className="w-full text-sm border-collapse" data-testid="excel-page-table">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-[11px] text-slate-500 border-b border-r border-slate-200 w-12 text-center">#</th>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-r border-slate-200 min-w-[120px]"
                  >
                    {h || `Col ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-amber-50/40">
                  <td className="px-2 py-1 text-[11px] text-slate-400 border-b border-r border-slate-100 text-center bg-slate-50">
                    {ri + 1}
                  </td>
                  {headers.map((_, ci) => (
                    <EditableCell
                      key={ci}
                      value={row[ci] ?? ""}
                      onCommit={(v) => updateCell(ri, ci, v)}
                      testid={`excel-cell-${ri}-${ci}`}
                    />
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={headers.length + 1} className="text-center py-8 text-sm text-slate-400">
                    No data rows in the source file
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-3 italic">
          Edits are kept in this tab only. Use <strong>Download Excel</strong> to save your changes.
          Closing this tab discards them.
        </p>
      </div>
    </div>
  );
};

// Single editable cell — read-only by default, becomes an input on double-click
const EditableCell = ({ value, onCommit, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
    setEditing(false);
  };

  if (editing) {
    return (
      <td className="border-b border-r border-slate-100 p-0">
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { setDraft(value); setEditing(false); }
          }}
          className="w-full px-3 py-1.5 text-sm bg-amber-50 outline-none ring-2 ring-amber-400"
          data-testid={`${testid}-input`}
        />
      </td>
    );
  }
  return (
    <td
      className="px-3 py-1.5 border-b border-r border-slate-100 cursor-text whitespace-nowrap"
      onDoubleClick={() => setEditing(true)}
      title="Double-click to edit"
      data-testid={testid}
    >
      {value}
    </td>
  );
};

export default BulkLabelExcelPage;
